import { collection, updateDoc, doc, getDocs, query, where } from "firebase/firestore";
import { db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import StorageService from "./storage_service";
import { employeeToFirestore } from "../models/employee";

/**
 * EmployeeService
 * Gerencia os Colaboradores (Funcionários) do sistema.
 * Colaboradores são usuários com acesso ao painel administrativo.
 * Integração entre Firestore (dados) e Cloud Functions (Auth).
 */
const EmployeeService = {
    /**
     * Lista todos os colaboradores de um tenant (exclui o owner).
     */
    getEmployees: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "users"),
                where("idTenant", "==", idTenant),
                where("role", "!=", "owner")
            );
            const querySnapshot = await getDocs(q);
            const employees = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                // O Form e Frontend esperam que `employee.role` seja o ID do cargo
                const frontendRole = (data.role === 'employee' && data.idRole) ? data.idRole : data.role;
                employees.push({ id: doc.id, ...data, role: frontendRole });
            });
            return employees;
        } catch (error) {
            console.error("Erro ao listar colaboradores:", error);
            throw error;
        }
    },

    /**
     * Lista TODOS os usuários de um tenant (inclui owner).
     * Usado em módulos onde qualquer usuário pode ser selecionado (ex: instrutor de turma).
     */
    getAllByTenant: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "users"),
                where("idTenant", "==", idTenant)
            );
            const querySnapshot = await getDocs(q);
            const users = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const frontendRole = (data.role === 'employee' && data.idRole) ? data.idRole : data.role;
                users.push({ id: docSnap.id, ...data, role: frontendRole });
            });
            return users;
        } catch (error) {
            console.error("Erro ao listar usuários do tenant:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um colaborador.
     * Centraliza a lógica de decisão entre CREATE (via Cloud Function) e UPDATE (via Firestore).
     */
    save: async (formData, idTenant, authUser, existingEmployees = [], roles = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        if (formData.id) {
            return await EmployeeService.update(formData.id, formData, idTenant, authUser, existingEmployees, roles);
        } else {
            return await EmployeeService.create(formData, idTenant, authUser, roles);
        }
    },

    /**
     * Cria um novo colaborador (Auth + Firestore) via Cloud Function.
     */
    create: async (employeeData, idTenant, authUser, roles = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório para criação.");

        try {
            // Verifica se o e-mail já está em uso
            const checkEmailExistsFn = httpsCallable(functions, 'checkEmailExists');
            const emailCheckResponse = await checkEmailExistsFn({ email: employeeData.email });
            if (emailCheckResponse.data.exists) {
                throw new Error("Este e-mail já está sendo utilizado por outro usuário.");
            }

            const createPlatformUserFn = httpsCallable(functions, 'createPlatformUser');

            // Sanitiza os dados via schema antes de enviar para a Cloud Function
            const payload = employeeToFirestore(employeeData, idTenant, authUser, roles);

            // Guarda a foto original e REMOVE do payload para não enviar base64 pesado à Cloud Function
            const originalPhoto = payload.photoUrl;
            const hasNewPhoto = originalPhoto && originalPhoto.startsWith('data:image');
            if (hasNewPhoto) {
                payload.photoUrl = null; // Será atualizado após o upload
            }

            const result = await createPlatformUserFn({
                ...payload,
                roleId: payload.idRole, // createPlatformUser espera 'roleId' como param
                password: employeeData.password // Mantém a senha para o Auth
            });

            const newUid = result.data?.uid;

            // UPLOAD DA FOTO NO STORAGE (após ter o uid)
            if (hasNewPhoto && newUid) {
                const storagePath = `tenants/${idTenant}/users/${newUid}/profile.jpg`;
                const downloadUrl = await StorageService.uploadBase64Image(storagePath, originalPhoto);
                // Atualiza o doc no Firestore com a URL do Storage
                const userRef = doc(db, "users", newUid);
                await updateDoc(userRef, { photoUrl: downloadUrl });
                payload.photoUrl = downloadUrl;
            }

            const fullName = payload.fullName;

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.EMPLOYEES,
                action: AUDIT_ACTIONS.CREATE,
                entityId: newUid,
                entityName: fullName,
                description: `Criou o colaborador "${fullName}"`
            });

            return { id: newUid, ...payload };
        } catch (error) {
            console.error("Erro ao criar colaborador:", error);
            throw error;
        }
    },

    /**
     * Atualiza dados de um colaborador diretamente no Firestore.
     */
    update: async (uid, data, idTenant, authUser, existingEmployees = [], roles = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório para atualização.");

        try {
            // UPLOAD DE FOTO NO STORAGE (mesmo padrão do MemberService)
            let finalPhotoUrl = data.photoUrl;
            if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image')) {
                const storagePath = `tenants/${idTenant}/users/${uid}/profile.jpg`;
                finalPhotoUrl = await StorageService.uploadBase64Image(storagePath, finalPhotoUrl);
                data.photoUrl = finalPhotoUrl;
            }

            const userRef = doc(db, "users", uid);
            const oldData = existingEmployees.find(e => e.id === uid) || {};

            // Sanitiza os dados via schema
            const payload = employeeToFirestore(data, idTenant, authUser, roles);

            await updateDoc(userRef, payload);

            // Auditoria
            const changes = AuditService.calculateDiff(oldData, payload);
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.EMPLOYEES,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: uid,
                entityName: payload.fullName,
                description: `Atualizou o colaborador "${payload.fullName}"`,
                details: changes
            });

            return { id: uid, ...payload };
        } catch (error) {
            console.error('[EmployeeService.update] Erro ao atualizar colaborador:', error);
            throw error;
        }
    }
};

export default EmployeeService;
